import { useMutation } from '@apollo/client';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { REPORT_POST } from '../../graphql/resolvers/feed/feedMutations/reportPost.gql';
import ButtonComponent from '../UI/Button/Button.component';
import './report-post-dialog.scss';

type ReportReason =
  | 'HARASSMENT'
  | 'HATE_SPEECH'
  | 'SEXUAL_CONTENT'
  | 'SELF_HARM'
  | 'CHILD_SAFETY'
  | 'UNLABELED_AI'
  | 'IMPERSONATION'
  | 'SCAM_FRAUD'
  | 'SPAM'
  | 'IP_THEFT'
  | 'UNDISCLOSED_ADS'
  | 'REGULATED_GOODS'
  | 'GRAPHIC_CONTENT'
  | 'OFF_TOPIC'
  | 'SPOILERS';

interface ReasonOption {
  value: ReportReason;
  label: string;
}

interface ReasonCategory {
  label: string;
  reasons: ReasonOption[];
}

const REASON_CATEGORIES: ReasonCategory[] = [
  {
    label: 'Safety',
    reasons: [
      { value: 'HARASSMENT', label: 'Harassment or bullying' },
      { value: 'HATE_SPEECH', label: 'Hate speech' },
      { value: 'SEXUAL_CONTENT', label: 'Sexual content or nudity' },
      { value: 'SELF_HARM', label: 'Self-harm or suicide' },
      { value: 'CHILD_SAFETY', label: 'Child safety' },
    ],
  },
  {
    label: 'Integrity',
    reasons: [
      { value: 'UNLABELED_AI', label: 'Unlabeled AI-generated content' },
      { value: 'IMPERSONATION', label: 'Impersonation' },
      { value: 'SCAM_FRAUD', label: 'Scam or fraud' },
      { value: 'SPAM', label: 'Spam' },
    ],
  },
  {
    label: 'Legal',
    reasons: [
      { value: 'IP_THEFT', label: 'Intellectual property theft' },
      { value: 'UNDISCLOSED_ADS', label: 'Undisclosed advertisement' },
      { value: 'REGULATED_GOODS', label: 'Regulated or illegal goods' },
    ],
  },
  {
    label: 'Quality',
    reasons: [
      { value: 'GRAPHIC_CONTENT', label: 'Graphic or disturbing content' },
      { value: 'OFF_TOPIC', label: 'Off-topic or irrelevant' },
      { value: 'SPOILERS', label: 'Spoilers' },
    ],
  },
];

interface ReportPostDialogProps {
  open: boolean;
  onClose: () => void;
  postId: string;
  onReported?: () => void;
}

const ReportPostDialog = ({
  open,
  onClose,
  postId,
  onReported,
}: ReportPostDialogProps) => {
  const [reason, setReason] = useState<ReportReason>('HARASSMENT');
  const [details, setDetails] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [reportPost, { loading }] = useMutation(REPORT_POST);

  useEffect(() => {
    if (open) {
      setReason('HARASSMENT');
      setDetails('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    setError(null);
    try {
      await reportPost({
        variables: {
          postId,
          reason,
          details: details.trim() || null,
        },
      });
      if (onReported) onReported();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit report');
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="report-dialog__overlay" />
        <Dialog.Content className="report-dialog__content">
          <div className="report-dialog__header">
            <Dialog.Title className="report-dialog__title">
              Report post
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                className="report-dialog__close"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <Dialog.Description className="report-dialog__description">
            Thanks for looking out for the community. Please tell us what's
            wrong with this post.
          </Dialog.Description>

          <div className="report-dialog__field">
            <label htmlFor="report-reason" className="report-dialog__label">
              Reason
            </label>
            <select
              id="report-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value as ReportReason)}
              className="report-dialog__select"
            >
              {REASON_CATEGORIES.map((category) => (
                <optgroup key={category.label} label={category.label}>
                  {category.reasons.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div className="report-dialog__field">
            <label htmlFor="report-details" className="report-dialog__label">
              Additional details (optional)
            </label>
            <textarea
              id="report-details"
              className="report-dialog__textarea"
              rows={4}
              maxLength={500}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Give us more context so we can review faster."
            />
          </div>

          {error && <p className="report-dialog__error">{error}</p>}

          <div className="report-dialog__actions">
            <ButtonComponent
              onClick={onClose}
              className="report-dialog__cancel"
              disabled={loading}
            >
              Cancel
            </ButtonComponent>
            <ButtonComponent
              onClick={handleSubmit}
              className="report-dialog__submit"
              disabled={loading}
            >
              {loading ? 'Submitting…' : 'Submit report'}
            </ButtonComponent>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ReportPostDialog;
